import {dispatch} from 'app/store'
import {endpointsActions} from 'features/Endpoints/endpointsSlice'
import {
  GetAnalyticsDetailFilter as SetAnalyticsDetailFilter,
  POLICY_TYPE,
  RESOURCE_TYPE,
} from 'interfaces'
import {history} from 'utils'
import {
  setProductivityTitle,
  setTopCategoriesTimeStamp,
} from '../Productivity/analyticsProductivityAction'
import {setAnalyticsDetailFilters} from '../Detail/analyticsDetailAction'
import chartRawOptions from './chartRawOptions.json'

export enum ViolationTypeFilters {
  Policy = 'policy',
  Malware = 'malware',
  CAC = 'CAC',
}

export const filterCommonClasses = [
  'font-bold',
  'uppercase',
  'rounded-full',
  'px-4',
  'border',
  'py-1',
  'text-xs',
  'abc-regular',
  'outline-none',
  'focus-visible:border-gray-300',
  'hover:border-gray-300',
]
export const selectedFilterClasses = [
  'bg-gray-800',
  'text-white',
  'border-gray-300',
]
export const nonSelectedFilterClasses = [
  'bg-gray-900',
  'text-gray-1100',
  'border-gray-900',
]
// this array sequence is important
export const policyTypeFilterLabels = [
  'all',
  ViolationTypeFilters.Policy,
  ViolationTypeFilters.Malware,
  ViolationTypeFilters.CAC,
]
export const treeMapChartColors = ['#D4A0FF', '#AF50FF', '#604CBF']
export const whiteColor = '#FFF'
export const numZero = 0,
  numOne = 1,
  numTwo = 2,
  numTen = 10,
  numThirty = 30,
  numFortyFive = 45

// 7 day trend initial state
export const a7DayTrendInitialState = {
  policy: {
    totalViolations: numZero,
    totalTransactions: numZero,
    details: [],
  },
  malware: {
    totalBlocks: numZero,
    filesScanned: numZero,
    details: [],
  },
  cloudAppControl: {
    totalBlocks: numZero,
    totalTransactions: numZero,
    details: [],
  },
  a7DayTrendBlocks: [],
}
export const a7DayTrendChartSeriesInitState = [
  {
    id: 'policy',
    name: 'Policy',
    data: [],
  },
  {
    id: 'malware',
    name: 'Malware',
    data: [],
  },
  {
    id: 'cloudAppControl',
    name: 'Cloud App Control',
    data: [],
  },
]

export const treeMapChartPatterns = [...chartRawOptions.treeMapChartPatterns]
export const treeMapRawOptions = (usersImported: boolean) => {
  return {
    ...chartRawOptions.treeMapRawOptions,
    plotOptions: {
      ...chartRawOptions.treeMapRawOptions.plotOptions,
      series: {
        ...chartRawOptions.treeMapRawOptions.plotOptions.series,
        events: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          click: function (event: any) {
            const options = event?.point?.options || {}
            const entityType = (options.entityType || '').toLowerCase()

            let resourceType: RESOURCE_TYPE = RESOURCE_TYPE.Users // A default value to suppress typescript warning
            let data: string = ''
            if (entityType === 'user') {
              resourceType = RESOURCE_TYPE.Users
              data = options.email
            } else if (entityType === 'group') {
              resourceType = RESOURCE_TYPE.Groups
              data = options.email
            } else if (entityType === 'location') {
              resourceType = RESOURCE_TYPE.Location
              data = options.name
            }
            usersImported &&
              handleResourceTypeViolationDetailClick(resourceType, data)
          },
        },
        dataLabels: {
          ...chartRawOptions.treeMapRawOptions.plotOptions.series.dataLabels,
          formatter: function () {
            // eslint-disable-next-line @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any
            const self: any = this
            return `<div data-cy='treemap-label-${(
              self?.point?.name || ''
            ).toLowerCase()}-${self?.point?.index}' class='text-white'>
              <div data-cy='treemap-label-name' class='text-xs karla-bold no-underline policy-treemap-chart-label'>${
                self?.point?.name || 'Loading...'
              }</div>
            </div>
            `
          },
        },
      },
    },
    tooltip: {
      ...chartRawOptions.treeMapRawOptions.tooltip,
      enabled: usersImported,
      formatter() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias, @typescript-eslint/no-explicit-any
        const self: any = this
        const policyViolations = self?.point?.policyViolations
        const malwareBlocks = self?.point?.malwareBlocks
        const cacBlocks = self?.point?.cacBlocks
        return `
      <div data-cy='treemap-tooltip-${self?.point?.name || ''.toLowerCase()}-${
          self?.point?.index
        }' class='text-white policy-treemap-chart-tooltip'>
        <div class='text-xs karla'>${self?.point?.name}</div>
          <p class="pt-1 text-xs karla">
            <div>${policyViolations ? `Policy - ${policyViolations}` : ''}</div>
            <div>${malwareBlocks ? `Malware - ${malwareBlocks}` : ''}</div>
            <div>${cacBlocks ? `CAC - ${cacBlocks}` : ''}</div>
          </p>
      </div>
      `
      },
    },
  }
}

export const handleResourceTypeViolationDetailClick = (
  resourceType: RESOURCE_TYPE,
  data?: string,
) => {
  let stateParams: SetAnalyticsDetailFilter
  switch (resourceType) {
    case RESOURCE_TYPE.Users:
      stateParams = {
        resourceType: [RESOURCE_TYPE.Users],
        filterDetails: {
          userEmail: data,
        },
      }
      break
    case RESOURCE_TYPE.Groups:
      stateParams = {
        resourceType: [RESOURCE_TYPE.Groups],
        filterDetails: {
          groupEmail: data,
        },
      }
      break
    case RESOURCE_TYPE.Location: {
      const [city = '', country = ''] = (data || '').split(',')
      stateParams = {
        resourceType: [RESOURCE_TYPE.Location],
        filterDetails: {
          city: city.trim(),
          country: country.trim(),
        },
      }
      break
    }
    default:
      stateParams = {}
      break
  }

  if (Object.keys(stateParams).length !== 0) {
    dispatch(setAnalyticsDetailFilters(stateParams))
    history.push('/analytics/detail')
  }
}

export const handleViolationDetailClick = (
  policyType: POLICY_TYPE,
  category?: string,
): void => {
  const statePayload: SetAnalyticsDetailFilter = {
    policyType: [policyType],
  }
  if (category) {
    statePayload.filterDetails = {
      category,
    }
  }
  dispatch(setAnalyticsDetailFilters(statePayload))
  history.push('/analytics/detail')
}

export const handlePolicyClick = () => {
  history.push('/analytics/policy')
}

export const handleProductivityClick = (category: string) => {
  dispatch(setProductivityTitle(category))
  dispatch(setTopCategoriesTimeStamp(Date.now()))
  history.push('/analytics/productivity')
}

export const handleShadowITClick = () => {
  history.push('/analytics/shadowit')
}

export const handleEndpointClick = (location: string) => {
  // dispatch an action
  dispatch(endpointsActions.setSelectedLocation(location))
  history.push('/endpoints')
}

export const thresholdToFormatNumber = 99999
